import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card.tsx';
import Dot from '@/components/ui/Dot.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { columnLabelClass } from '@/components/ui/typography.ts';
import { PendingChangeVm } from '@/features/dashboard/bookingChanges/types/PublicChangeViewModel.ts';
import ClientAcceptanceBalancePanel from '@/features/dashboard/bookingChanges/components/flow/ClientAcceptanceBalancePanel.tsx';
import {
  PENDING_CHANGE_AMENDMENT,
  PENDING_CHANGE_FOOTNOTE,
  PENDING_CHANGE_HEADING,
  PENDING_CHANGE_INTRO,
  pendingChangeSettlementSentence,
} from './pendingChangeCopy.ts';

interface PendingChangeCardProps extends React.HTMLAttributes<HTMLDivElement> {
  change: PendingChangeVm;
  onAccept: () => void;
  onReject: () => void;
  isDeciding: boolean;
}

/**
 * Panel zmian czekających na zgodę zapisującego — pierwsza karta podsumowania
 * rezerwacji, bo jako jedyna czeka na decyzję.
 *
 * Wiersz bez wartości niesie sam fakt zmiany („Zmiana opcji dodatkowej”):
 * wariant zdjęty z oferty po wystawieniu paczki nie ma już nazwy, a UUID nie
 * powie klientowi nic. Wartość sprzed zmiany stoi przekreślona przed nową —
 * mają ją wybory z katalogu; przy pozostałych pozycjach kontrakt jej nie niesie
 * i wtedy zostaje sama nowa wartość, zamiast zmyślonej kreski.
 */
const PendingChangeCard = ({
  change,
  onAccept,
  onReject,
  isDeciding,
  className,
  ...props
}: PendingChangeCardProps) => (
  <Card highlighted className={cn('overflow-hidden', className)} {...props}>
    <div className="border-b border-neutral-100 px-[18px] py-4">
      <div className="flex items-center gap-2.5">
        <Dot size="lg" />
        <h2 className="text-[17px] font-extrabold text-ink">
          {PENDING_CHANGE_HEADING}
        </h2>
      </div>
      <p className="mt-1.5 text-pretty text-sm text-neutral-600">
        {PENDING_CHANGE_INTRO}
      </p>
    </div>

    <div className="flex flex-col gap-4 px-[18px] py-4">
      {change.groups.map((group) => (
        <div key={group.id}>
          <div className={columnLabelClass}>{group.personName}</div>
          <div className="mt-1.5 flex flex-col gap-1.5">
            {group.lines.map((line) => (
              <div
                key={line.id}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[15px]"
              >
                <span className="text-neutral-600">{line.label}</span>
                <span className="ml-auto flex flex-wrap items-baseline justify-end gap-x-1.5">
                  {line.previousValue && (
                    <span className="wrap-anywhere text-neutral-400 line-through">
                      {line.previousValue}
                    </span>
                  )}
                  {line.value && (
                    <span className="wrap-anywhere font-semibold text-ink">
                      {line.value}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <ClientAcceptanceBalancePanel
        balance={change.balance}
        settlementDescription={pendingChangeSettlementSentence(change.balance)}
      />

      {change.requiresAmendment && (
        <p className="text-pretty text-sm text-neutral-600">
          {PENDING_CHANGE_AMENDMENT}
        </p>
      )}
    </div>

    <div className="flex flex-col gap-3 border-t border-neutral-100 px-[18px] py-4">
      <div className="flex flex-wrap gap-3">
        <ActionButton
          tone="success"
          size="lg"
          onClick={onAccept}
          isLoading={isDeciding}
        >
          Akceptuję zmiany
        </ActionButton>
        <ActionButton
          tone="outline"
          size="lg"
          onClick={onReject}
          disabled={isDeciding}
        >
          Odrzucam zmiany
        </ActionButton>
      </div>
      <p className="text-[13px] text-neutral-400">{PENDING_CHANGE_FOOTNOTE}</p>
    </div>
  </Card>
);

export default PendingChangeCard;
